// =====================================================================================
// INVRT Radar — web console — account — Credits tab
//
// Buying a credit pack through Stripe's hosted checkout. Moved verbatim out of
// AccountPage.tsx in the C1 split; the page shell registers it under the tab id
// "credits", which the "top up credits" links elsewhere in the console
// (/account?tab=credits) depend on.
//
// Every figure on a pack card is the API response, formatted — never a hard-coded price
// or credit count. Nothing is charged here: the browser leaves for Stripe's own page.
// =====================================================================================

import { useMutation } from "@tanstack/react-query";
import { CreditCard } from "lucide-react";
import { useApiClient, useBalance, usePacks } from "@/hooks/useApi";
import { useDeviceFingerprint } from "@/hooks/useDeviceFingerprint";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  formatCredits,
  formatCreditsWithUnit,
  formatEffectiveRate,
  formatMinorCurrency,
} from "@/lib/format";
import { redirectExternally } from "@/lib/navigation";
import type { CheckoutResponse } from "@/lib/types";

export function CreditsPanel() {
  const api = useApiClient();
  const balance = useBalance();
  const packs = usePacks();
  const deviceFingerprint = useDeviceFingerprint();

  const checkout = useMutation({
    mutationFn: (packSlug: string) => {
      const origin = window.location.origin;
      return api.post<CheckoutResponse>("/packs/checkout", {
        pack_slug: packSlug,
        success_url: `${origin}/account/checkout/success`,
        cancel_url: `${origin}/account/checkout/cancelled`,
        ...(deviceFingerprint ? { device_fingerprint: deviceFingerprint } : {}),
      });
    },
    onSuccess: (result) => {
      // Nothing has been charged yet — Stripe's own page is where a card is entered. See
      // src/lib/navigation.ts for why this goes through a seam rather than a bare
      // window.location.assign() call here.
      redirectExternally(result.checkout_url);
    },
  });

  if (packs.isLoading) return <LoadingState label="Loading the credit packs…" />;
  if (packs.isError) {
    return <ErrorState error={packs.error} onRetry={() => void packs.refetch()} />;
  }
  if (!packs.data) return null;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader className="flex-row flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Buy credits</CardTitle>
            <p className="mt-1 text-sm text-ink-muted">
              Card payment through Stripe. Each price below is exactly what you will be
              charged; the per-credit figure alongside it is a rounded guide for comparing
              packs.
            </p>
          </div>
          <Badge tone="info">
            Balance {balance.isLoading ? "…" : formatCredits(balance.data?.balance)}
          </Badge>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {packs.data.packs.length === 0 ? (
            <EmptyState
              title="No packs are on sale yet"
              body="INVRT has not switched on any credit packs for your organisation yet. Check back shortly."
            />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {packs.data.packs.map((pack) => {
                const isThisPack = checkout.variables === pack.slug;
                return (
                  <Card key={pack.slug} className="border-line">
                    <CardHeader>
                      <CardTitle>{pack.name}</CardTitle>
                      <p className="text-2xl font-semibold text-ink">
                        {formatMinorCurrency(pack.price_minor, pack.currency)}
                      </p>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-3">
                      <p className="text-sm text-ink-muted">
                        {formatCreditsWithUnit(pack.credits)}
                      </p>
                      <p className="text-xs text-ink-faint">
                        {formatEffectiveRate(pack.price_minor, pack.credits, pack.currency)}
                      </p>
                      <Button
                        variant="commit"
                        loading={checkout.isPending && isThisPack}
                        disabled={checkout.isPending && !isThisPack}
                        onClick={() => checkout.mutate(pack.slug)}
                      >
                        <CreditCard className="h-4 w-4" aria-hidden="true" />
                        Buy {pack.name}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
          {checkout.isError ? <ErrorState error={checkout.error} /> : null}
        </CardContent>
      </Card>
    </div>
  );
}
